/**
 * Notifications settings tab - email alerts and preferences
 */
import { useState } from 'react'
import { useUserPreferences, useUpdateUserPreferences } from '../../hooks'
import { Button, LoadingCard } from '../common'
import type { DigestFrequency, AlertPriority } from '../../types'

const DIGEST_OPTIONS: { value: DigestFrequency; label: string; description: string }[] = [
  { value: 'daily', label: 'Daily', description: 'Get a summary email once per day' },
  { value: 'weekly', label: 'Weekly', description: 'Get a summary email once per week' },
  { value: 'none', label: 'None', description: 'No summary emails (only urgent alerts)' },
]

const PRIORITY_OPTIONS: { value: AlertPriority; label: string; description: string }[] = [
  { value: 'urgent', label: 'Urgent Only', description: 'Only receive critical alerts' },
  { value: 'high', label: 'High & Above', description: 'Receive high priority and urgent alerts' },
  { value: 'normal', label: 'Normal & Above', description: 'Receive most alerts (recommended)' },
  { value: 'low', label: 'All Alerts', description: 'Receive all alerts including low priority' },
]

export function NotificationsTab() {
  const { data: preferences, isLoading } = useUserPreferences()
  const updatePreferences = useUpdateUserPreferences()

  const [localPrefs, setLocalPrefs] = useState<{
    email_alerts_enabled: boolean
    email_digest_frequency: DigestFrequency
    alert_priority_threshold: AlertPriority
  } | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [error, setError] = useState('')

  // Initialize local state from fetched preferences
  const currentPrefs = localPrefs || (preferences ? {
    email_alerts_enabled: preferences.email_alerts_enabled,
    email_digest_frequency: preferences.email_digest_frequency,
    alert_priority_threshold: preferences.alert_priority_threshold,
  } : null)

  const handleChange = <K extends keyof NonNullable<typeof currentPrefs>>(
    key: K,
    value: NonNullable<typeof currentPrefs>[K]
  ) => {
    if (!currentPrefs) return
    setLocalPrefs({ ...currentPrefs, [key]: value })
    setHasChanges(true)
    setError('')
  }

  const handleSave = async () => {
    if (!localPrefs) return
    setError('')
    try {
      await updatePreferences.mutateAsync(localPrefs)
      setHasChanges(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preferences')
    }
  }

  const handleReset = () => {
    setLocalPrefs(null)
    setHasChanges(false)
    setError('')
  }

  if (isLoading || !currentPrefs) {
    return <LoadingCard />
  }

  return (
    <div className="space-y-6">
      {/* Email Alerts Toggle */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Email Notifications</h2>
        </div>
        <div className="px-6 py-4 space-y-6">
          {/* Master Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Enable Email Alerts</h3>
              <p className="text-sm text-gray-500">Receive email notifications for rule alerts</p>
            </div>
            <button
              type="button"
              onClick={() => handleChange('email_alerts_enabled', !currentPrefs.email_alerts_enabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                currentPrefs.email_alerts_enabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  currentPrefs.email_alerts_enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Digest Frequency */}
          {currentPrefs.email_alerts_enabled && (
            <>
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Email Digest Frequency</h3>
                <div className="space-y-2">
                  {DIGEST_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-start p-3 rounded-lg border cursor-pointer ${
                        currentPrefs.email_digest_frequency === option.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="digest"
                        value={option.value}
                        checked={currentPrefs.email_digest_frequency === option.value}
                        onChange={() => handleChange('email_digest_frequency', option.value)}
                        className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                        <span className="block text-sm text-gray-500">{option.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Alert Priority Threshold */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Alert Priority Threshold</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Only receive alerts at or above this priority level
                </p>
                <div className="space-y-2">
                  {PRIORITY_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-start p-3 rounded-lg border cursor-pointer ${
                        currentPrefs.alert_priority_threshold === option.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="priority"
                        value={option.value}
                        checked={currentPrefs.alert_priority_threshold === option.value}
                        onChange={() => handleChange('alert_priority_threshold', option.value)}
                        className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                        <span className="block text-sm text-gray-500">{option.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Save Bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3 shadow-lg">
          <Button variant="secondary" onClick={handleReset}>
            Reset
          </Button>
          <Button onClick={handleSave} isLoading={updatePreferences.isPending}>
            Save Changes
          </Button>
          {error && <p className="text-sm text-red-600 self-center">{error}</p>}
        </div>
      )}
    </div>
  )
}
